import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Lightbox } from 'ngx-lightbox';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import Contact from 'src/app/model/Contact/contact';
import ProductAsset from 'src/app/model/product-asset';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import { IP_BOARD_PART_TYPE_CHOICES } from 'src/app/model/product/ip-board/ip-board-part-type';
import { Project } from 'src/app/model/project/project';
import { SignShapeTypesChoices } from 'src/app/model/sign/enums/sign-shape-type';
import { SignTypesChoices } from 'src/app/model/sign/enums/sign-type';
import { FormatPhoneNumber } from 'src/app/modules/app-pipes/format-phonenumber';
import {
  ProductAssetsChangedEvent,
  ProjectDisplayObservableService,
} from 'src/app/services/project-display-observable.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { ClientSignSelectorHelper } from '../../client-project-form/base-project/client-ip-board-form/client-sign-selector/client-sign-selector-helper';
import { FilePickerComponent } from '../file-picker/file-picker.component';
import {
  IPBoardDesignPickerHelper,
  PhoneNumberWithOptions,
} from './ip-board-design-picker-helper';
import { FileHelper } from '../../../../helpers/file-helper';

@Component({
  selector: 'ip-board-design-picker',
  templateUrl: './ip-board-design-picker.component.html',
  styleUrls: ['./ip-board-design-picker.component.scss'],
  providers: [FormatPhoneNumber],
})
export class IPBoardDesignPickerComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  @Input()
  ipBoard: IPBoard;

  @Input()
  project: Project;

  @Input()
  client: boolean = false;

  @Input()
  oldSystemImmo: boolean = false;

  @ViewChild(FilePickerComponent, { static: false })
  filePickerComponent: FilePickerComponent;

  @Output()
  save = new EventEmitter<any>();

  displayImmoscoopOption: boolean = false;
  displaySpottoOption = false;
  displayAssetStyles: any[];
  pdfList: ProductAsset[];

  otherContacts: Contact[];
  boardPartPhoneNumberWithOptions: PhoneNumberWithOptions[];

  SIGN_TYPE_CHOICES = SignTypesChoices;
  SIGN_SHAPE_TYPE_CHOICES = SignShapeTypesChoices;
  IP_BOARD_PART_TYPE_CHOICES = IP_BOARD_PART_TYPE_CHOICES;

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private _lightbox: Lightbox,
    private formatPhoneNumber: FormatPhoneNumber
  ) {
    super();
  }

  ngOnInit(): void {
    this.calculateDisplayAssetStyles();
    this.calculateDisplayImmoscoopAndSpottoOption();
    this.calculateIPBoardPartPhoneNumbersWithOptions();

    this.addSubscription(
      this.projectDisplayObservableService.getProjectContactsChangedSubscribable(),
      () => {
        this.calculateDisplayImmoscoopAndSpottoOption();
        this.calculateIPBoardPartPhoneNumbersWithOptions();
      }
    );

    this.addSubscription(
      this.projectDisplayObservableService.getSelectedOtherContactsChangedSubscribable(),
      (otherContacts: Contact[]) => {
        this.otherContacts = otherContacts;
        this.calculateIPBoardPartPhoneNumbersWithOptions();
      }
    );

    this.addSubscription(
      this.projectDisplayObservableService.getProductAssetsChangedSubscribable(),
      (event: ProductAssetsChangedEvent) => {
        if (
          event.productId === this.ipBoard.id &&
          event.formSection === 'extraDesign'
        ) {
          this.calculateDisplayAssetStyles();
        }
      }
    );
  }

  generalExtraInfoChanged(value: string): void {
    this.ipBoard.generalExtraInfo = value;
    this.save.emit();
  }

  immoscoopLogoChanged(value: boolean): void {
    this.ipBoard.immoscoopLogo = value;
    this.save.emit();
  }

  spottoLogoChanged(value: boolean): void {
    this.ipBoard.spottoLogo = value;
    this.save.emit();
  }

  draftDesignChanged(): void {
    this.projectDisplayObservableService
      .getIPBoardConfigurationChangedSubscribable()
      .next({
        ipBoardPart: this.ipBoard.boardPart,
      });
    this.save.emit();
  }

  displayAssetsChanged(): void {
    this.calculateDisplayAssetStyles();
    this.projectDisplayObservableService
      .getDesignAssetsChangedSubscribable()
      .next();
  }

  private calculateDisplayImmoscoopAndSpottoOption(): void {
    this.displayImmoscoopOption =
      ProjectHelper.displayImmoscoopOptionForProjectContacts(
        this.project.projectContacts
      );
    this.displaySpottoOption =
      ProjectHelper.displaySpottoOptionForProjectContacts(
        this.project.projectContacts
      );
    if (
      this.displayImmoscoopOption &&
      !this.displaySpottoOption &&
      this.ipBoard.immoscoopLogo == null
    ) {
      this.ipBoard.immoscoopLogo = true;
    }
    if (
      this.displaySpottoOption &&
      !this.displayImmoscoopOption &&
      this.ipBoard.spottoLogo == null
    ) {
      this.ipBoard.spottoLogo = true;
    }
  }

  private calculateIPBoardPartPhoneNumbersWithOptions(): void {
    this.boardPartPhoneNumberWithOptions =
      IPBoardDesignPickerHelper.calculateIPBoardPartPhoneNumbers(
        this.ipBoard.boardPart,
        this.project,
        this.otherContacts,
        this.formatPhoneNumber
      );
    this.ipBoard.boardPart.phoneNumbers =
      this.boardPartPhoneNumberWithOptions.map((o) => o.ipBoardPartPhoneNumber);
  }

  private calculateDisplayAssetStyles(): void {
    if (!CollectionHelper.isPopulated(this.ipBoard.productAssets)) {
      this.displayAssetStyles = null;
      return;
    }
    const styles: StyleWithIndex[] = [];
    const pdfs: StyleWithIndex[] = [];
    this.ipBoard.productAssets.forEach((pa, i) => {
      if (
        ProductHelper.canDisplayProductAsset(pa) &&
        ProductHelper.isDesignWithExternalAsset(pa)
      ) {
        styles.push({
          style: ClientSignSelectorHelper.getBackgroundImageStyle(
            `/api/assets/${pa.asset.uuid}`
          ),
          productAsset: pa,
          index: i,
        });
      } else if (
        !pa.archived &&
        pa.asset != null &&
        FileHelper.isPdf(pa.asset) &&
        ProductHelper.isDesignWithExternalAsset(pa)
      ) {
        pdfs.push({ style: null, productAsset: pa, index: i });
      }
    });
    this.displayAssetStyles = styles;
    this.pdfList = pdfs;
  }

  openLightbox(productAsset: ProductAsset): void {
    this._lightbox.open([
      {
        src: `/api/assets/${productAsset.asset.uuid}`,
        thumb: `/api/assets/${productAsset.asset.uuid}`,
      },
    ]);
  }

  deleteGraphicsFile($event: MouseEvent, style: StyleWithIndex): void {
    $event.stopPropagation();
    this.filePickerComponent.removeFile(style.productAsset, style.index);
  }

  removePdf(pdfWithIndex: StyleWithIndex): void {
    this.filePickerComponent.removeFile(
      pdfWithIndex.productAsset,
      pdfWithIndex.index
    );
  }
}

export interface StyleWithIndex {
  style: any;
  productAsset: ProductAsset;
  index: number;
}
